import clsx from 'clsx';
import { memo, useRef } from 'react';

import type { ToolbarItem } from '../types';

import { IconButton } from '../../Button/subcomponents/IconButton';
import { CountBadge } from '../../CountBadge/CountBadge';
import { Layout } from '../../Layout/Layout';
import { useMountTransition } from '../hooks';

interface Props {
  disabled: boolean;
  isActive: boolean;
  item: ToolbarItem;
  onSelectItem: (item: ToolbarItem) => void;
  tooltipText?: string;
}

export const ToolbarItemButton = memo(
  ({ disabled, isActive, item, onSelectItem, tooltipText }: Props) => {
    const hasCount = item.count !== undefined && item.count > 0;
    const { isVisible, onExitTransitionEnd, shouldRender } =
      useMountTransition(hasCount);
    // Freezes the last positive count so the badge keeps its number while it
    // fades out, instead of `CountBadge` itself unmounting the instant the
    // count drops to zero.
    const lastCountRef = useRef(item.count ?? 0);
    if (hasCount) {
      lastCountRef.current = item.count as number;
    }

    return (
      <Layout className="eds-toolbar--item" flex="none">
        <IconButton
          aria-current={isActive ? true : undefined}
          icon={item.icon}
          label={item.label}
          disabled={disabled}
          tooltip={
            tooltipText ? { text: tooltipText, direction: 'left' } : undefined
          }
          variant="tertiary"
          onClick={() => onSelectItem(item)}
        />
        {shouldRender && (
          <Layout
            className={clsx(
              'eds-toolbar--item-badge',
              isVisible && 'eds-toolbar--item-badge--visible',
            )}
            flex="none"
            onTransitionEnd={onExitTransitionEnd}
          >
            <CountBadge
              count={lastCountRef.current}
              maxCount={item.maxCount}
              size="s"
            />
          </Layout>
        )}
      </Layout>
    );
  },
);

ToolbarItemButton.displayName = 'ToolbarItemButton';
